import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, ClipboardList, Loader2, Save, Wand2, Search } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'position_title', label: 'Position Title', type: 'input', ph: 'Brand Ambassador / Lead Generation Specialist' },
  { key: 'duties', label: 'Job Duties & Responsibilities', type: 'textarea', rows: 14, ph: 'Full standard duties that appear on every booking…' },
  { key: 'important_notes', label: 'Important Notes', type: 'textarea', rows: 4, ph: 'Key reminder shown on each booking…' },
  { key: 'onsite_contact_name', label: 'On-site Contact Name', type: 'input', ph: 'Corey or Mel' },
  { key: 'onsite_contact_title', label: 'On-site Contact Title', type: 'input', ph: 'Guardian Garage Floors' },
  { key: 'onsite_contact_phone', label: 'On-site Contact Phone', type: 'input', ph: '(770) 864-8448' },
  { key: 'backup_contact_name', label: 'Backup Contact Name', type: 'input', ph: 'Mel' },
  { key: 'backup_contact_phone', label: 'Backup Contact Phone', type: 'input', ph: '(404) 981-7674' },
  { key: 'contact_instructions', label: 'Contact Instructions', type: 'textarea', rows: 2, ph: 'Point of contact is always…' },
];

const empty = () => FIELDS.reduce((a, f) => ({ ...a, [f.key]: '' }), {});

export default function ClientBookingDefaults() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  // Platform-wide Staffing Manager default (Mel) — editable in one place.
  const [sm, setSm] = useState({ name: '', title: '', phone: '' });
  const [smSaving, setSmSaving] = useState(false);
  const [smBackfilling, setSmBackfilling] = useState(false);

  const loadSm = useCallback(async () => {
    try {
      const r = await api.get('/admin/client-booking-defaults/platform/staffing-manager');
      setSm({ name: r.data.name || '', title: r.data.title || '', phone: r.data.phone || '' });
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadSm(); }, [loadSm]);

  const saveSm = async () => {
    if (!sm.name.trim()) { toast.error('Staffing Manager name is required'); return; }
    setSmSaving(true);
    try {
      await api.put('/admin/client-booking-defaults/platform/staffing-manager', sm);
      toast.success('Default Staffing Manager saved — new gigs will use it');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSmSaving(false); }
  };

  const backfillSm = async () => {
    setSmBackfilling(true);
    try {
      const r = await api.post('/admin/client-booking-defaults/platform/staffing-manager/backfill');
      toast.success(`Applied to ${r.data.updated} upcoming gig(s) with no Staffing Manager`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply'); }
    finally { setSmBackfilling(false); }
  };

  const loadClients = useCallback(async (query) => {
    try {
      const r = await api.get('/admin/client-booking-defaults/clients', { params: query ? { q: query } : {} });
      setClients(r.data.clients || []);
    } catch { toast.error('Could not load clients'); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadDefaults = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/client-booking-defaults/${id}`);
      setMeta(r.data);
      setForm({ ...empty(), ...Object.fromEntries(Object.entries(r.data.defaults || {}).map(([k, v]) => [k, v || ''])) });
    } catch { toast.error('Could not load defaults'); }
    finally { setLoading(false); }
  }, []);

  const onSelect = (id) => { setClientId(id); loadDefaults(id); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/client-booking-defaults/${clientId}`, form);
      toast.success('Booking defaults saved');
      loadClients(q);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const applyToExisting = async (overwrite) => {
    setApplying(true);
    try {
      const r = await api.post(`/admin/client-booking-defaults/${clientId}/apply`, null, { params: { overwrite } });
      toast.success(`Applied to ${r.data.updated} existing booking(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply'); }
    finally { setApplying(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-booking-defaults-page">
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-5 py-6">
          <button onClick={() => navigate('/admin/home')} className="flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-4" data-testid="cbd-back">
            <ArrowLeft className="w-4 h-4" /> Admin Home
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-indigo-500/15 border border-indigo-500/30">
              <ClipboardList className="w-6 h-6 text-indigo-300" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Client Booking Defaults</h1>
              <p className="text-slate-400 text-sm">Standard job duties & point-of-contact that auto-apply to every booking</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-6 space-y-5">
        <Card className="border-indigo-200"><CardContent className="py-4 px-5 space-y-3" data-testid="staffing-manager-default-card">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-indigo-500" />
            <h2 className="text-sm font-semibold text-slate-800">Platform Staffing Manager (default on every gig)</h2>
          </div>
          <p className="text-xs text-slate-500">
            This administrative contact is applied automatically to every new gig (and can be re-applied to upcoming gigs).
            It is separate from the on-site contact and is never placed in the on-site field.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input className="mt-1" value={sm.name} placeholder="Mel"
                onChange={(e) => setSm((s) => ({ ...s, name: e.target.value }))} data-testid="sm-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Title</Label>
              <Input className="mt-1" value={sm.title} placeholder="Staffing Manager"
                onChange={(e) => setSm((s) => ({ ...s, title: e.target.value }))} data-testid="sm-title" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Phone</Label>
              <Input className="mt-1" value={sm.phone} placeholder="404.981.7674"
                onChange={(e) => setSm((s) => ({ ...s, phone: e.target.value }))} data-testid="sm-phone" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveSm} disabled={smSaving} data-testid="sm-save">
              {smSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save default
            </Button>
            <Button variant="outline" onClick={backfillSm} disabled={smBackfilling} data-testid="sm-backfill">
              {smBackfilling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Apply to upcoming gigs (empty only)
            </Button>
          </div>
        </CardContent></Card>

        <Card><CardContent className="py-4 px-5 space-y-3">
          <Label className="text-xs text-slate-500">Client</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8" placeholder="Search company…" value={q}
                onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadClients(q)}
                data-testid="cbd-search" />
            </div>
            <Select value={clientId} onValueChange={onSelect}>
              <SelectTrigger className="w-72" data-testid="cbd-client-select"><SelectValue placeholder="Select a client…" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>
                    {c.company_name || c.email}{c.has_defaults ? '  ✓' : ''}{c.is_test ? ' (test)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {meta && (
            <p className="text-xs text-slate-500">
              {meta.company_name} · {meta.shift_count} booking(s)
              {' '}<Badge variant="outline" className="ml-1">{Object.values(meta.defaults || {}).some(Boolean) ? 'Has defaults' : 'No defaults yet'}</Badge>
            </p>
          )}
        </CardContent></Card>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : clientId ? (
          <>
            <Card><CardContent className="py-4 px-5 space-y-4" data-testid="cbd-form">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-slate-500">{f.label}</Label>
                  {f.type === 'textarea' ? (
                    <Textarea className="mt-1" rows={f.rows || 3} placeholder={f.ph}
                      value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}
                      data-testid={`cbd-${f.key}`} />
                  ) : (
                    <Input className="mt-1" placeholder={f.ph} value={form[f.key]}
                      onChange={(e) => set(f.key, e.target.value)} data-testid={`cbd-${f.key}`} />
                  )}
                </div>
              ))}
            </CardContent></Card>

            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={saving} data-testid="cbd-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save defaults
              </Button>
              <Button variant="outline" onClick={() => applyToExisting(false)} disabled={applying} data-testid="cbd-apply-empty">
                {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Fill empty fields on existing bookings
              </Button>
              <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => applyToExisting(true)} disabled={applying} data-testid="cbd-apply-overwrite">
                Overwrite all existing bookings
              </Button>
            </div>
            <p className="text-xs text-slate-400">
              New bookings inherit these automatically (any create path). "Fill empty" only touches blank fields on existing bookings; "Overwrite all" replaces them.
            </p>
          </>
        ) : (
          <Card><CardContent className="py-16 text-center text-slate-500" data-testid="cbd-empty">
            Select a client to view or edit their standard booking defaults.
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
